import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Chip,
    Tooltip
} from '@mui/material';
import {
    EmojiEvents as EmojiEventsIcon,
    Timer as TimerIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import useApp from "../../store/contexts/AppContext";

// Styled components
const BonusCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'visible',
    boxShadow: theme.shadows[2],
}));

const BonusCardHighlight = styled(Box)(({ theme, accountType }) => {
    const getBonusColor = () => {
        switch (accountType) {
            case 'standard':
                return '#1976d2'; // Blue
            case 'premium':
                return '#d44402'; // Orange
            case 'elite':
                return '#aa00ff'; // Purple
            default:
                return '#757575'; // Gray for basic
        }
    };

    return {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '8px',
        borderRadius: `${theme.spacing(1.5)}px ${theme.spacing(1.5)}px 0 0`,
        backgroundColor: getBonusColor(),
    };
});

const BonusButton = styled(Button)(({ theme, accountType, disabled }) => {
    const getBonusColor = () => {
        switch (accountType) {
            case 'standard':
                return '#1976d2'; // Blue
            case 'premium':
                return '#d44402'; // Orange
            case 'elite':
                return '#aa00ff'; // Purple
            default:
                return '#43a047'; // Green for basic
        }
    };

    return {
        backgroundColor: disabled ? theme.palette.action.disabledBackground : getBonusColor(),
        color: disabled ? theme.palette.action.disabled : 'white',
        '&:hover': {
            backgroundColor: disabled ? theme.palette.action.disabledBackground : getBonusColor(),
            opacity: 0.9,
        },
    };
});

const MultiplierChip = styled(Chip)(({ theme, accountType }) => {
    const getBonusColor = () => {
        switch (accountType) {
            case 'standard':
                return '#1976d2'; // Blue
            case 'premium':
                return '#d44402'; // Orange
            case 'elite':
                return '#aa00ff'; // Purple
            default:
                return '#43a047'; // Green for basic
        }
    };

    return {
        backgroundColor: getBonusColor(),
        color: 'white',
        fontWeight: 'bold',
        position: 'absolute',
        top: -10,
        right: 15,
        zIndex: 1,
        '& .MuiChip-icon': {
            color: 'white !important',
        },
    };
});

// Function to format time until next claim
const formatTimeUntilNextClaim = (nextClaimTime) => {
    if (!nextClaimTime) return '';

    const now = new Date();
    const next = new Date(nextClaimTime);
    const diffMs = next - now;

    if (diffMs <= 0) return 'Available now';

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    let timeText = '';
    if (diffHrs > 0) {
        timeText += `${diffHrs}h `;
    }
    timeText += `${diffMins}m`;

    return timeText;
};

// Helper functions - will use settings from backend
const getBonusMultiplier = (accountType, settings) => {
    if (!settings || !settings.daily_bonus) return '1x';
    const multipliers = settings.daily_bonus.multipliers;
    const multiplier = multipliers[accountType] || multipliers.basic || 0.5;
    return `${multiplier}x`;
};

const getBonusAmount = (accountType, settings) => {
    if (!settings || !settings.daily_bonus) return 1; // Minimal fallback
    const baseAmount = settings.daily_bonus.base_amount || 2;
    const multipliers = settings.daily_bonus.multipliers;
    const multiplier = multipliers[accountType] || multipliers.basic || 0.5;
    return Math.floor(baseAmount * multiplier);
};

export default function DailyBonusWidget({ classes }) {
    const {
        appState: { userData },
        claimDailyBonusApiCall,
        checkDailyBonusStatusApiCall,
    } = useApp();

    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [economySettings, setEconomySettings] = useState(null);
    const [bonusStatus, setBonusStatus] = useState({
        can_claim: false,
        bonus_amount: 0,
        next_claim_time: null,
        last_claimed: null
    });

    // Get account type from user data
    const accountType = userData?.account_type || 'basic';

    // Calculate bonus amount
    const bonusAmount = bonusStatus.bonus_amount || getBonusAmount(accountType, economySettings);

    // Get multiplier text
    const multiplierText = getBonusMultiplier(accountType, economySettings);

    // Fetch economy settings on mount
    useEffect(() => {
        const fetchEconomySettings = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/economy/settings`
                );
                if (response.data && response.data.data) {
                    setEconomySettings(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching economy settings:", error);
                // Will use fallback values from helper functions
            }
        };
        fetchEconomySettings();
    }, []);

    // Check for daily bonus status on component mount
    useEffect(() => {
        let isMounted = true;
        const checkBonusStatus = async () => {
            try {
                setCheckingStatus(true);
                setApiError(false);

                // Add timeout to prevent infinite loading
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Timeout after 5 seconds')), 5000)
                );

                // Race between the API call and timeout
                const response = await Promise.race([
                    checkDailyBonusStatusApiCall(),
                    timeoutPromise
                ]);

                if (isMounted) {
                    if (response && response.success) {
                        // API returns snake_case fields, set them directly
                        setBonusStatus(response.data);
                    } else {
                        // If API returns but without success, show fallback data
                        setApiError(true);
                        setBonusStatus({
                            can_claim: true, // Assume can claim as default
                            bonus_amount: getBonusAmount(accountType, economySettings),
                            next_claim_time: null,
                            last_claimed: null
                        });
                    }
                }
            } catch (error) {
                console.error("Error checking bonus status:", error);
                if (isMounted) {
                    setApiError(true);
                    // Set fallback data on error
                    setBonusStatus({
                        can_claim: true, // Assume can claim as default
                        bonus_amount: getBonusAmount(accountType, economySettings),
                        next_claim_time: null,
                        last_claimed: null
                    });
                }
            } finally {
                if (isMounted) {
                    setCheckingStatus(false);
                }
            }
        };

        if (userData?.id) {
            checkBonusStatus();
        } else {
            // If no user data, stop loading
            setCheckingStatus(false);
        }

        return () => {
            isMounted = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set up a timer to update the countdown
    useEffect(() => {
        if (!bonusStatus.can_claim && bonusStatus.next_claim_time) {
            const timer = setInterval(() => {
                const now = new Date();
                const next = new Date(bonusStatus.next_claim_time);

                if (now >= next) {
                    setBonusStatus(prev => ({ ...prev, can_claim: true }));
                    clearInterval(timer);
                }
            }, 30000); // Check every 30 seconds

            return () => clearInterval(timer);
        }
    }, [bonusStatus.can_claim, bonusStatus.next_claim_time]);

    // Handle claiming the daily bonus
    const handleClaimBonus = async () => {
        if (loading || !bonusStatus.can_claim) return;

        try {
            setLoading(true);

            // Add timeout to prevent infinite loading
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Timeout after 5 seconds')), 5000)
            );

            // Race between the API call and timeout
            const response = await Promise.race([
                claimDailyBonusApiCall(),
                timeoutPromise
            ]);

            if (response && response.success) {
                setBonusStatus({
                    can_claim: false,
                    bonus_amount: response.data.bonus_amount,
                    next_claim_time: response.data.next_claim,
                    last_claimed: new Date().toISOString()
                });
            } else {
                // Handle unsuccessful response
                setApiError(true);
            }
        } catch (error) {
            console.error("Error claiming bonus:", error);
            setApiError(true);
        } finally {
            setLoading(false);
        }
    };

    if (checkingStatus) {
        return (
            <BonusCard>
                <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Checking bonus status...
                    </Typography>
                </CardContent>
            </BonusCard>
        );
    }

    return (
        <BonusCard>
            <BonusCardHighlight accountType={accountType} />
            <MultiplierChip
                label={multiplierText}
                size="small"
                accountType={accountType}
                icon={<EmojiEventsIcon />}
            />

            <CardContent sx={{ pt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmojiEventsIcon sx={{ fontSize: '1.5rem', mr: 1, color: 'gold' }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        Daily Bonus
                    </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {apiError ?
                        "Daily bonus system is temporarily unavailable. Try again later." :
                        bonusStatus.can_claim
                            ? `Claim ${bonusAmount} coins for today's daily bonus!`
                            : `Your next daily bonus of ${bonusAmount} coins will be available soon.`
                    }
                </Typography>

                {bonusStatus.can_claim ? (
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleClaimBonus}
                        disabled={loading || apiError}
                        component={BonusButton}
                        accountType={accountType}
                        sx={{ py: 1 }}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <EmojiEventsIcon />}
                    >
                        {loading ? "Claiming..." : apiError ? "Not Available" : `Claim ${bonusAmount} Coins`}
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Tooltip title="Next available at midnight">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TimerIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {formatTimeUntilNextClaim(bonusStatus.next_claim_time)}
                                </Typography>
                            </Box>
                        </Tooltip>

                        {bonusStatus.last_claimed && (
                            <Tooltip title={`Last claimed: ${new Date(bonusStatus.last_claimed).toLocaleString()}`}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CheckCircleIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'success.main' }} />
                                    <Typography variant="body2" color="success.main">
                                        Claimed today
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </CardContent>
        </BonusCard>
    );
}